import {
  EnvironmentProviders,
  inject,
  Injectable,
  makeEnvironmentProviders,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { marker as _ } from '@jsverse/transloco-keys-manager/marker';
import { tap } from 'rxjs';

export function provideMaterial(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    {
      provide: MatPaginatorIntl,
      useClass: TranslateMatPaginatorIntl,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' } satisfies MatFormFieldDefaultOptions,
    },
  ]);
}

@Injectable()
export class TranslateMatPaginatorIntl extends MatPaginatorIntl {
  private readonly translateService = inject(TranslocoService);

  constructor() {
    super();
    this.translateService.langChanges$
      .pipe(
        tap(() => {
          this.itemsPerPageLabel = this.translateService.translate(
            _('i18n.Paginator.ItemsPerPage'),
          );
          this.nextPageLabel = this.translateService.translate(
            _('i18n.Paginator.NextPage'),
          );
          this.previousPageLabel = this.translateService.translate(
            _('i18n.Paginator.PreviousPage'),
          );
          this.firstPageLabel = this.translateService.translate(
            _('i18n.Paginator.FirstPage'),
          );
          this.lastPageLabel = this.translateService.translate(
            _('i18n.Paginator.LastPage'),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.changes.next());
  }

  override getRangeLabel = (
    page: number,
    pageSize: number,
    length: number,
  ): string => {
    let start = 0;
    let end = length;

    if (length !== 0 && pageSize !== 0) {
      start = page * pageSize + 1;
      end = Math.min(page * pageSize + pageSize, length);
    }
    return this.translateService.translate(_('i18n.Paginator.Range'), {
      start,
      end,
      length,
    });
  };
}
